import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Mail, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeData: {
    to?: string;
    subject?: string;
    body?: string;
  };
  onSave: (config: { to: string; subject: string; body: string }) => void;
}

const SendEmailModal: React.FC<SendEmailModalProps> = ({ isOpen, onClose, nodeData, onSave }) => {
  const [to, setTo] = useState(nodeData.to || '');
  const [subject, setSubject] = useState(nodeData.subject || '');
  const [body, setBody] = useState(nodeData.body || '');

  useEffect(() => {
    if (isOpen) {
      setTo(nodeData.to || '');
      setSubject(nodeData.subject || '');
      setBody(nodeData.body || '');
    }
  }, [isOpen, nodeData]);

  const handleSave = () => {
    if (!to || !subject || !body) {
      alert('Preencha todos os campos obrigatórios!');
      return;
    }
    onSave({ to, subject, body });
    onClose();
  };

  const insertVariable = (field: 'to' | 'subject' | 'body', variable: string) => {
    const setter = field === 'to' ? setTo : field === 'subject' ? setSubject : setBody;
    const current = field === 'to' ? to : field === 'subject' ? subject : body;
    setter(current + `{{${variable}}}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <ModalContainer
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <ModalHeader>
              <HeaderContent>
                <IconWrapper>
                  <Mail size={24} />
                </IconWrapper>
                <div>
                  <ModalTitle>Configurar Envio de Email</ModalTitle>
                  <ModalSubtitle>Configure o destinatário, assunto e corpo do email</ModalSubtitle>
                </div>
              </HeaderContent>
              <CloseButton onClick={onClose}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <FormGroup>
                <Label>
                  Destinatário <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  placeholder="email@exemplo.com ou {{lead.email}}"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
                <VariablesHint>
                  <Sparkles size={12} />
                  Variáveis:
                  <VariableChip onClick={() => insertVariable('to', 'lead.email')}>
                    {`{{lead.email}}`}
                  </VariableChip>
                  <VariableChip onClick={() => insertVariable('to', 'trigger.email')}>
                    {`{{trigger.email}}`}
                  </VariableChip>
                </VariablesHint>
              </FormGroup>

              <FormGroup>
                <Label>
                  Assunto <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  placeholder="Bem-vindo ao Loopra CRM!"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <VariablesHint>
                  <Sparkles size={12} />
                  Variáveis:
                  <VariableChip onClick={() => insertVariable('subject', 'lead.nome')}>
                    {`{{lead.nome}}`}
                  </VariableChip>
                  <VariableChip onClick={() => insertVariable('subject', 'trigger.subject')}>
                    {`{{trigger.subject}}`}
                  </VariableChip>
                </VariablesHint>
              </FormGroup>

              <FormGroup>
                <Label>
                  Corpo do Email <Required>*</Required>
                </Label>
                <TextArea
                  placeholder="Olá {{lead.nome}},&#10;&#10;Bem-vindo ao Loopra CRM!&#10;&#10;Atenciosamente,&#10;Equipe Loopra"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                />
                <VariablesHint>
                  <Sparkles size={12} />
                  Variáveis:
                  <VariableChip onClick={() => insertVariable('body', 'lead.nome')}>
                    {`{{lead.nome}}`}
                  </VariableChip>
                  <VariableChip onClick={() => insertVariable('body', 'lead.email')}>
                    {`{{lead.email}}`}
                  </VariableChip>
                  <VariableChip onClick={() => insertVariable('body', 'lead.telefone')}>
                    {`{{lead.telefone}}`}
                  </VariableChip>
                </VariablesHint>
              </FormGroup>

              <PreviewSection>
                <PreviewLabel>Preview:</PreviewLabel>
                <PreviewBox>
                  <PreviewTo><strong>Para:</strong> {to || '(vazio)'}</PreviewTo>
                  <PreviewSubject><strong>Assunto:</strong> {subject || '(vazio)'}</PreviewSubject>
                  <PreviewBody>{body || '(vazio)'}</PreviewBody>
                </PreviewBox>
              </PreviewSection>
            </ModalBody>

            <ModalFooter>
              <CancelButton onClick={onClose}>Cancelar</CancelButton>
              <SaveButton onClick={handleSave}>Salvar Configuração</SaveButton>
            </ModalFooter>
          </ModalContainer>
        </>
      )}
    </AnimatePresence>
  );
};

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 9998;
`;

const ModalContainer = styled(motion.div)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 12px;
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing[1]} 0 0 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing[6]};
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 4px;

    &:hover {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.error};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  resize: vertical;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const VariablesHint = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-wrap: wrap;
`;

const VariableChip = styled.button`
  background: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-family: 'Courier New', monospace;
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}40;
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const PreviewSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
`;

const PreviewLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const PreviewBox = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
`;

const PreviewTo = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const PreviewSubject = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const PreviewBody = styled.div`
  margin-top: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  white-space: pre-wrap;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`;

const CancelButton = styled.button`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.border};
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const SaveButton = styled.button`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }
`;

export default SendEmailModal;
